/**
 * Reports catalog — built-in + user-custom reports, list columns, and sort prefs.
 * Persistence mirrors demand/debtor list column storage.
 */

import {
  loadPersistedSort,
  savePersistedSort,
  sortRowsByColumn,
} from './listColumnSort.js';

const CUSTOM_STORAGE_KEY = 'demandjet.customReports';
const COLUMNS_STORAGE_KEY = 'demandjet.reportsListColumns';
const SORT_STORAGE_KEY = 'demandjet.reportsListSort';

/** Metric building blocks a custom report can include. */
export const REPORT_METRIC_OPTIONS = Object.freeze([
  {
    id: 'collected.week',
    group: 'Collected',
    label: 'Collected this week',
  },
  {
    id: 'collected.month',
    group: 'Collected',
    label: 'Collected this month',
  },
  {
    id: 'collected.year',
    group: 'Collected',
    label: 'Collected this year',
  },
  {
    id: 'volume.open',
    group: 'Volume',
    label: 'Open demands',
  },
  {
    id: 'volume.closed',
    group: 'Volume',
    label: 'Closed demands',
  },
  {
    id: 'volume.archived',
    group: 'Volume',
    label: 'Archived demands',
  },
  {
    id: 'volume.combined',
    group: 'Volume',
    label: 'Total demands',
  },
  {
    id: 'volume.debtors',
    group: 'Volume',
    label: 'Debtors',
  },
  {
    id: 'status',
    group: 'Status',
    label: 'Demands by status',
  },
  {
    id: 'aging',
    group: 'Operations',
    label: 'Aging (days open)',
  },
  {
    id: 'attorney',
    group: 'Operations',
    label: 'Attorney workload',
  },
  {
    id: 'payments',
    group: 'Operations',
    label: 'Payment activity',
  },
  {
    id: 'timeToPaid',
    group: 'Operations',
    label: 'Time to paid',
  },
  {
    id: 'stalled',
    group: 'Operations',
    label: 'Stalled pipeline',
  },
  {
    id: 'evidenceGaps',
    group: 'Operations',
    label: 'Evidence gaps',
  },
  {
    id: 'messagesUnread',
    group: 'Operations',
    label: 'Messages needing reply',
  },
  {
    id: 'intake',
    group: 'Operations',
    label: 'New intake',
  },
]);

export const BUILTIN_REPORTS = Object.freeze([
  {
    id: 'collected',
    name: 'Collected',
    type: 'builtin',
    description: 'Payment ledger totals for this week, month, and year.',
    metricIds: ['collected.week', 'collected.month', 'collected.year'],
  },
  {
    id: 'volume',
    name: 'Volume',
    type: 'builtin',
    description: 'Portfolio headcount — open, closed, archived, total demands, and debtors.',
    metricIds: [
      'volume.open',
      'volume.closed',
      'volume.archived',
      'volume.combined',
      'volume.debtors',
    ],
  },
  {
    id: 'status',
    name: 'By status',
    type: 'builtin',
    description: 'Demand count, amount owed, and average time in each status.',
    metricIds: ['status'],
  },
  {
    id: 'pipeline',
    name: 'Pipeline',
    type: 'builtin',
    description: 'Active demands only — counts and dollars by pipeline stage.',
    metricIds: ['status'],
  },
  {
    id: 'collections',
    name: 'Collections',
    type: 'builtin',
    description: 'Recovered vs outstanding balances on active demands.',
    metricIds: ['volume.closed', 'volume.open'],
  },
  {
    id: 'aging',
    name: 'Aging',
    type: 'builtin',
    description: 'Counts and open balances by days since created.',
    metricIds: ['aging'],
  },
  {
    id: 'attorney',
    name: 'Attorney workload',
    type: 'builtin',
    description: 'Open demands and dollars grouped by attorney partner.',
    metricIds: ['attorney'],
  },
  {
    id: 'payments',
    name: 'Payment activity',
    type: 'builtin',
    description: 'Recent payment ledger activity for this week and month.',
    metricIds: ['payments'],
  },
  {
    id: 'time-to-paid',
    name: 'Time to paid',
    type: 'builtin',
    description: 'Average time from demand created to paid.',
    metricIds: ['timeToPaid'],
  },
  {
    id: 'stalled',
    name: 'Stalled pipeline',
    type: 'builtin',
    description: 'Open demands with no status change for 14+ days.',
    metricIds: ['stalled'],
  },
  {
    id: 'evidence-gaps',
    name: 'Evidence gaps',
    type: 'builtin',
    description: 'Open demands with no evidence files attached.',
    metricIds: ['evidenceGaps'],
  },
  {
    id: 'messages-unread',
    name: 'Messages needing reply',
    type: 'builtin',
    description: 'Demand conversations with unread attorney or teammate activity.',
    metricIds: ['messagesUnread'],
  },
  {
    id: 'intake',
    name: 'New intake',
    type: 'builtin',
    description: 'Demands created this week and month, plus current New Case volume.',
    metricIds: ['intake'],
  },
]);

export const REPORT_LIST_COLUMNS = Object.freeze([
  {
    id: 'name',
    label: 'Report',
    required: true,
    defaultVisible: true,
    sortType: 'string',
  },
  {
    id: 'type',
    label: 'Type',
    defaultVisible: true,
    sortType: 'string',
  },
  {
    id: 'description',
    label: 'Description',
    defaultVisible: true,
    sortType: 'string',
  },
  {
    id: 'metrics',
    label: 'Metrics',
    defaultVisible: true,
    sortType: 'string',
  },
  {
    id: 'updatedAt',
    label: 'Updated',
    defaultVisible: true,
    sortType: 'date',
  },
]);

const DEFAULT_SORT = Object.freeze({ columnId: 'name', direction: 'asc' });

function metricLabel(id) {
  return REPORT_METRIC_OPTIONS.find((m) => m.id === id)?.label || id;
}

function normalizeMetricIds(ids) {
  const allowed = new Set(REPORT_METRIC_OPTIONS.map((m) => m.id));
  const seen = new Set();
  const out = [];
  for (const raw of ids || []) {
    const id = String(raw || '');
    if (!allowed.has(id) || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

function normalizeCustomReport(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const id = String(raw.id || '').trim();
  const name = String(raw.name || '').trim();
  const metricIds = normalizeMetricIds(raw.metricIds);
  if (!id.startsWith('custom-') || !name || !metricIds.length) return null;
  const description = String(raw.description || '').trim();
  const updatedAt = String(raw.updatedAt || raw.createdAt || '') || new Date().toISOString();
  const createdAt = String(raw.createdAt || updatedAt);
  return {
    id,
    name,
    type: 'custom',
    description:
      description ||
      metricIds
        .slice(0, 3)
        .map(metricLabel)
        .join(', ') + (metricIds.length > 3 ? '…' : ''),
    metricIds,
    createdAt,
    updatedAt,
  };
}

export function loadCustomReports() {
  try {
    const raw = localStorage.getItem(CUSTOM_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map(normalizeCustomReport).filter(Boolean);
  } catch {
    return [];
  }
}

export function saveCustomReports(list) {
  const next = (list || []).map(normalizeCustomReport).filter(Boolean);
  try {
    localStorage.setItem(CUSTOM_STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Ignore quota / private-mode failures.
  }
  return next;
}

export function createCustomReportId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `custom-${crypto.randomUUID()}`;
  }
  return `custom-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

export function upsertCustomReport(input) {
  const existing = loadCustomReports();
  const now = new Date().toISOString();
  const id = String(input?.id || '').startsWith('custom-')
    ? String(input.id)
    : createCustomReportId();
  const prev = existing.find((r) => r.id === id);
  const nextReport = normalizeCustomReport({
    id,
    name: input?.name,
    description: input?.description,
    metricIds: input?.metricIds,
    createdAt: prev?.createdAt || now,
    updatedAt: now,
  });
  if (!nextReport) return { reports: existing, report: null };
  const reports = prev
    ? existing.map((r) => (r.id === id ? nextReport : r))
    : [...existing, nextReport];
  return { reports: saveCustomReports(reports), report: nextReport };
}

export function deleteCustomReport(id) {
  const target = String(id || '');
  const reports = saveCustomReports(loadCustomReports().filter((r) => r.id !== target));
  return reports;
}

/** Full catalog: built-ins first (stable order), then customs by updatedAt desc. */
export function listReportCatalog(customReports = loadCustomReports()) {
  const customs = [...(customReports || [])].sort((a, b) =>
    String(b.updatedAt || '').localeCompare(String(a.updatedAt || ''))
  );
  return [...BUILTIN_REPORTS.map((r) => ({ ...r })), ...customs];
}

export function findReportById(id, customReports = loadCustomReports()) {
  const key = String(id || '');
  return listReportCatalog(customReports).find((r) => r.id === key) || null;
}

export function reportTypeLabel(report) {
  return report?.type === 'custom' ? 'Custom' : 'Built-in';
}

export function reportMetricsSummary(report) {
  const ids = report?.metricIds || [];
  if (!ids.length) return '—';
  if (ids.includes('status') && ids.length === 1) return 'By status';
  return ids.map(metricLabel).join(' · ');
}

export function reportCellValue(report, columnId) {
  switch (columnId) {
    case 'name':
      return report?.name || '';
    case 'type':
      return reportTypeLabel(report);
    case 'description':
      return report?.description || '';
    case 'metrics':
      return reportMetricsSummary(report);
    case 'updatedAt': {
      if (report?.type !== 'custom') return '—';
      const ms = Date.parse(String(report.updatedAt || ''));
      if (!Number.isFinite(ms)) return '—';
      return new Date(ms).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    }
    default:
      return '';
  }
}

export function reportSortValue(report, columnId) {
  if (columnId === 'updatedAt') {
    if (report?.type !== 'custom') return 0;
    return Date.parse(String(report.updatedAt || '')) || 0;
  }
  return reportCellValue(report, columnId);
}

export function sortReports(reports, sort) {
  return sortRowsByColumn(
    reports,
    sort,
    (row, columnId) => reportSortValue(row, columnId),
    (row) => String(row?.id || '')
  );
}

export function reportSortFieldLabel(columnId) {
  return REPORT_LIST_COLUMNS.find((c) => c.id === columnId)?.label || columnId;
}

export function defaultVisibleReportColumnIds() {
  return REPORT_LIST_COLUMNS.filter((c) => c.defaultVisible).map((c) => c.id);
}

export function loadVisibleReportColumnIds() {
  const allowed = new Set(REPORT_LIST_COLUMNS.map((c) => c.id));
  const required = REPORT_LIST_COLUMNS.filter((c) => c.required).map((c) => c.id);
  try {
    const raw = localStorage.getItem(COLUMNS_STORAGE_KEY);
    if (!raw) return defaultVisibleReportColumnIds();
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return defaultVisibleReportColumnIds();
    const ids = parsed.map(String).filter((id) => allowed.has(id));
    for (const id of required) {
      if (!ids.includes(id)) ids.unshift(id);
    }
    return ids.length ? ids : defaultVisibleReportColumnIds();
  } catch {
    return defaultVisibleReportColumnIds();
  }
}

export function saveVisibleReportColumnIds(ids) {
  const allowed = new Set(REPORT_LIST_COLUMNS.map((c) => c.id));
  const required = REPORT_LIST_COLUMNS.filter((c) => c.required).map((c) => c.id);
  const next = [];
  for (const id of ids || []) {
    if (allowed.has(id) && !next.includes(id)) next.push(id);
  }
  for (const id of required) {
    if (!next.includes(id)) next.unshift(id);
  }
  try {
    localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(next));
  } catch {
    // ignore
  }
  return next.length ? next : defaultVisibleReportColumnIds();
}

export function visibleReportColumns(visibleIds) {
  const order = visibleIds?.length ? visibleIds : defaultVisibleReportColumnIds();
  return order
    .map((id) => REPORT_LIST_COLUMNS.find((c) => c.id === id))
    .filter(Boolean);
}

export function loadReportsListSort() {
  return loadPersistedSort(
    SORT_STORAGE_KEY,
    DEFAULT_SORT,
    (id) => REPORT_LIST_COLUMNS.some((c) => c.id === id)
  );
}

export function saveReportsListSort(sort) {
  return savePersistedSort(SORT_STORAGE_KEY, sort);
}

function csvEscape(value) {
  const text = String(value ?? '');
  if (/[",\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
}

function blankCsvCell(value) {
  if (value == null || value === '') return '';
  return value;
}

export function reportRowsHaveDetail(rows = []) {
  return (rows || []).some((row) => row?.detail != null && String(row.detail).trim() !== '');
}

/** CSV text for report metric ledger rows (Metric, Count, Amount[, Detail]). */
export function reportRowsToCsv(rows = []) {
  const list = rows || [];
  const includeDetail = reportRowsHaveDetail(list);
  const header = (
    includeDetail ? ['Metric', 'Count', 'Amount', 'Detail'] : ['Metric', 'Count', 'Amount']
  )
    .map(csvEscape)
    .join(',');
  const lines = list.map((row) => {
    const cells = [row.label ?? '', blankCsvCell(row.count), blankCsvCell(row.amount)];
    if (includeDetail) cells.push(blankCsvCell(row.detail));
    return cells.map(csvEscape).join(',');
  });
  return [header, ...lines].join('\n');
}

export function reportDownloadFilename(
  reportName,
  stamp = new Date().toISOString().slice(0, 10),
  extension = 'csv'
) {
  const slug =
    String(reportName || 'report')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'report';
  const ext = String(extension || 'csv').replace(/^\./, '') || 'csv';
  return `demandjet-report-${slug}-${stamp}.${ext}`;
}

function triggerBlobDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

/** Trigger a CSV download of report rows. Returns row count. */
export function exportReportCsv(rows = [], { reportName, filename } = {}) {
  const list = rows || [];
  const csv = reportRowsToCsv(list);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  triggerBlobDownload(blob, filename || reportDownloadFilename(reportName, undefined, 'csv'));
  return list.length;
}

/** Trigger a PDF download of report rows. Returns row count. */
export async function exportReportPdf(
  rows = [],
  { reportName, description, filename, generatedAt } = {}
) {
  const list = rows || [];
  const { buildReportPdf, loadReportLetterheadLogo } = await import('./reportPdf.js');
  const logo = await loadReportLetterheadLogo();
  const bytes = buildReportPdf(list, { reportName, description, generatedAt, logo });
  const blob = new Blob([bytes], { type: 'application/pdf' });
  triggerBlobDownload(blob, filename || reportDownloadFilename(reportName, undefined, 'pdf'));
  return list.length;
}

/**
 * Resolve metric rows for a report from a buildClientReports() snapshot.
 * Each row uses explicit count / amount / detail fields for the ledger table.
 */
export function resolveReportMetricRows(report, snapshot) {
  const rows = [];
  for (const metricId of report?.metricIds || []) {
    if (metricId === 'collected.week') {
      rows.push({
        id: metricId,
        label: 'This week',
        count: null,
        amount: snapshot?.collected?.week?.label || '—',
        detail: 'Payment ledger since Sunday',
      });
      continue;
    }
    if (metricId === 'collected.month') {
      rows.push({
        id: metricId,
        label: 'This month',
        count: null,
        amount: snapshot?.collected?.month?.label || '—',
        detail: 'Payment ledger since the 1st',
      });
      continue;
    }
    if (metricId === 'collected.year') {
      rows.push({
        id: metricId,
        label: 'This year',
        count: null,
        amount: snapshot?.collected?.year?.label || '—',
        detail: 'Payment ledger since Jan 1',
      });
      continue;
    }
    if (metricId === 'volume.open') {
      rows.push({
        id: metricId,
        label: 'Open demands',
        count: snapshot?.volume?.open ?? 0,
        amount: null,
      });
      continue;
    }
    if (metricId === 'volume.closed') {
      rows.push({
        id: metricId,
        label: 'Closed demands',
        count: snapshot?.volume?.closed ?? 0,
        amount: null,
      });
      continue;
    }
    if (metricId === 'volume.archived') {
      rows.push({
        id: metricId,
        label: 'Archived',
        count: snapshot?.volume?.archived ?? 0,
        amount: null,
      });
      continue;
    }
    if (metricId === 'volume.combined') {
      rows.push({
        id: metricId,
        label: 'Total demands',
        count: snapshot?.volume?.combined ?? 0,
        amount: null,
      });
      continue;
    }
    if (metricId === 'volume.debtors') {
      rows.push({
        id: metricId,
        label: 'Debtors',
        count: snapshot?.volume?.debtors ?? 0,
        amount: null,
      });
      continue;
    }
    if (metricId === 'status') {
      for (const status of snapshot?.byStatus || []) {
        const detail =
          status.id === 'PAID'
            ? null
            : status.averageSamples
              ? `Avg ${status.averageOpenLabel} · ${status.averageSamples} stay${
                  status.averageSamples === 1 ? '' : 's'
                }`
              : `Avg ${status.averageOpenLabel}`;
        rows.push({
          id: `status.${status.id}`,
          label: status.label,
          count: status.count,
          amount: status.amountLabel,
          detail,
        });
      }
      continue;
    }
    if (metricId === 'aging') {
      const aging = snapshot?.aging;
      // Explanation lives in the Aging report subtitle — no Detail column values.
      rows.push({
        id: 'aging.open',
        label: 'Open demands',
        count: aging?.openCount ?? 0,
        amount: aging?.openAmountLabel || '—',
      });
      for (const bucket of aging?.buckets || []) {
        rows.push({
          id: `aging.${bucket.id}`,
          label: bucket.label,
          count: bucket.count,
          amount: bucket.amountLabel,
        });
      }
      continue;
    }
    if (metricId === 'attorney') {
      const attorney = snapshot?.attorney;
      if (!attorney?.rows?.length) {
        rows.push({
          id: 'attorney.empty',
          label: 'Attorney workload',
          count: 0,
          amount: null,
          detail: 'No open demands',
        });
      } else {
        for (const row of attorney.rows) {
          rows.push({
            id: `attorney.${row.id}`,
            label: row.label,
            count: row.count,
            amount: row.amountLabel,
          });
        }
      }
      continue;
    }
    if (metricId === 'payments') {
      const payments = snapshot?.payments;
      rows.push({
        id: 'payments.week',
        label: 'Payments this week',
        count: payments?.weekCount ?? 0,
        amount: payments?.weekAmountLabel || '—',
      });
      rows.push({
        id: 'payments.month',
        label: 'Payments this month',
        count: payments?.monthCount ?? 0,
        amount: payments?.monthAmountLabel || '—',
      });
      for (const row of payments?.recent || []) {
        rows.push({
          id: `payments.recent.${row.id}`,
          label: row.label,
          count: null,
          amount: row.value,
          detail: row.meta,
        });
      }
      continue;
    }
    if (metricId === 'timeToPaid') {
      const ttp = snapshot?.timeToPaid;
      rows.push({
        id: 'timeToPaid.avg',
        label: 'Average time to paid',
        count: ttp?.samples || null,
        amount: null,
        detail: ttp?.samples
          ? `Average ${ttp.averageLabel || '—'}`
          : 'No paid demands with dates yet',
      });
      continue;
    }
    if (metricId === 'stalled') {
      const stalled = snapshot?.stalled;
      rows.push({
        id: 'stalled.summary',
        label: `Stalled ${stalled?.thresholdDays ?? 14}+ days`,
        count: stalled?.count ?? 0,
        amount: stalled?.amountLabel || '—',
      });
      for (const row of stalled?.rows || []) {
        rows.push({
          id: `stalled.${row.id}`,
          label: row.meta || row.label,
          count: null,
          amount: null,
          detail: `Idle ${row.value} · ${row.label}`,
        });
      }
      continue;
    }
    if (metricId === 'evidenceGaps') {
      const gaps = snapshot?.evidenceGaps;
      rows.push({
        id: 'evidenceGaps.count',
        label: 'Open demands missing evidence',
        count: gaps?.count ?? 0,
        amount: gaps?.amountLabel || '—',
        detail: `${gaps?.openCount ?? 0} open demands total`,
      });
      continue;
    }
    if (metricId === 'messagesUnread') {
      const messages = snapshot?.messagesUnread;
      rows.push({
        id: 'messagesUnread.count',
        label: 'Conversations needing reply',
        count: messages?.count ?? 0,
        amount: null,
        detail: `${messages?.threadCount ?? 0} total conversations`,
      });
      continue;
    }
    if (metricId === 'intake') {
      const intake = snapshot?.intake;
      rows.push({
        id: 'intake.week',
        label: 'Created this week',
        count: intake?.weekCount ?? 0,
        amount: intake?.weekAmountLabel || '—',
      });
      rows.push({
        id: 'intake.month',
        label: 'Created this month',
        count: intake?.monthCount ?? 0,
        amount: intake?.monthAmountLabel || '—',
      });
      rows.push({
        id: 'intake.newCase',
        label: 'Currently New Case',
        count: intake?.newCaseCount ?? 0,
        amount: intake?.newCaseAmountLabel || '—',
      });
    }
  }
  return rows;
}
